import axios from "axios";
import type {
  IConnectionTestResult,
  IWorkspaceAIProviderFieldErrors,
} from "@/types/workspace-ai-provider";

/**
 * What may be logged about a failed request on this page — and nothing else.
 *
 * The request body of a test or save carries the provider credential in
 * plaintext, and an AxiosError keeps that body on `config.data`. Logging the
 * error object itself would put the key in the browser console and in any error
 * sink that reads console output. So every log site here logs this summary
 * instead: status, the SDK's error code and message, the URL — never the body,
 * never the headers.
 */
export function loggableError(error: unknown): {
  status: number | null;
  code: string | null;
  message: string;
  url: string | null;
} {
  if (axios.isAxiosError(error)) {
    return {
      status: error.response?.status ?? null,
      code: error.code ?? null,
      message: error.message,
      url: error.config?.url ?? null,
    };
  }
  return {
    status: null,
    code: null,
    message: error instanceof Error ? error.message : String(error),
    url: null,
  };
}

/** The backend refused a save because a model probe failed; `result` says which. */
export class AIProviderSaveRefusedError extends Error {
  constructor(readonly result: IConnectionTestResult) {
    super("The AI provider connection test failed; nothing was saved.");
    this.name = "AIProviderSaveRefusedError";
  }
}

/** A 400: one or more inputs were rejected before any provider call. */
export class AIProviderValidationError extends Error {
  constructor(readonly fieldErrors: IWorkspaceAIProviderFieldErrors) {
    super("The AI provider configuration has invalid fields.");
    this.name = "AIProviderValidationError";
  }
}

/** A 409: the stored credential changed while this request was being tested. */
export class AIProviderCredentialChangedError extends Error {
  constructor() {
    super("The stored credential changed while it was being tested; repeat the request.");
    this.name = "AIProviderCredentialChangedError";
  }
}

/**
 * Turn the API's three documented refusals of a test or save into typed errors
 * the AI provider form branches on (NG-1362); anything else is logged and
 * returned as it was, for the generic toast.
 */
export function aiProviderRefusal(
  error: unknown,
  slug: string,
  operation: "test" | "save",
): unknown {
  if (axios.isAxiosError(error) && error.response) {
    const { status, data } = error.response;
    if (status === 422 && operation === "save") {
      return new AIProviderSaveRefusedError(data as IConnectionTestResult);
    }
    if (status === 400) {
      return new AIProviderValidationError(data as IWorkspaceAIProviderFieldErrors);
    }
    if (status === 409) {
      return new AIProviderCredentialChangedError();
    }
  }
  console.error(`Workspace AI provider ${operation} failed`, {
    error: loggableError(error),
    slug,
  });
  return error;
}
